import React from "react";
import { User, Briefcase, Clock, Check } from "lucide-react";
import { useProfileContext } from '@/contexts/ProfileContext';
import { useUnifiedPortfolioQuery } from '@/hooks/portfolio/useUnifiedPortfolioQuery';
import { useNavigate } from 'react-router-dom';

const checklist = [
  {
    label: "About Me",
    description: "Write a short bio to help people learn more about you",
    icon: <User className="h-6 w-6 text-gray-400" />,
    key: "aboutMeComplete",
    route: "/dashboard/profile"
  },
  {
    label: "Portfolio",
    description: "Upload at least 3 projects to complete your profile",
    icon: <Briefcase className="h-6 w-6 text-gray-400" />,
    key: "portfolioComplete",
    route: "/dashboard/portfolio"
  },
  {
    label: "Experience",
    description: "Add one or more jobs you’ve had even freelance",
    icon: <Clock className="h-6 w-6 text-gray-400" />,
    key: "experienceComplete",
    route: "/dashboard/experience"
  },
];

const ProfileCompletionCardV2: React.FC = () => {
  const { profile, isLoading } = useProfileContext();
  const { data: unifiedItems = [], isLoading: portfolioLoading } = useUnifiedPortfolioQuery({
    profileId: profile?.id,
    includeDrafts: true,
    enabled: !!profile?.id
  });
  const navigate = useNavigate();

  // Check if we have any portfolio items (shots or case studies)
  const hasPortfolioItems = unifiedItems.length > 0;

  if (isLoading || !profile || portfolioLoading) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-200 p-8 animate-pulse">
        <div className="mb-6">
          <div className="font-bold text-xl text-black">Profile Completion</div>
          <div className="text-gray-500 text-base mt-1">Complete your profile to get discovered</div>
        </div>
        <div className="flex items-center mb-8">
          <div className="flex-1 mr-4">
            <div className="bg-white border border-gray-200 rounded-full w-full h-5 flex items-center">
              <div className="w-full h-full p-1">
                <div className="h-full bg-gray-200 rounded-full w-1/2" style={{ width: '50%' }}></div>
              </div>
            </div>
          </div>
          <span className="text-gray-700 font-semibold text-base min-w-[40px]">--%</span>
        </div>
        <div className="space-y-5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center justify-between p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                  <div className="h-6 w-6 bg-gray-200 rounded-full" />
                </span>
                <div>
                  <div className="font-bold text-lg text-black bg-gray-200 rounded w-24 h-4 mb-2" />
                  <div className="text-gray-500 text-base mt-1 bg-gray-100 rounded w-40 h-3" />
                </div>
              </div>
              <div className="h-6 w-6 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const aboutMeComplete = Boolean(profile.bio && profile.bio.trim().length > 0);
  const portfolioComplete = hasPortfolioItems;
  const experienceComplete = Boolean(profile.experience_items && profile.experience_items.length > 0);
  const completionItems = [aboutMeComplete, portfolioComplete, experienceComplete];
  const completedItems = completionItems.filter(Boolean).length;
  const completionPercent = Math.round((completedItems / completionItems.length) * 100);
  const status = { aboutMeComplete, portfolioComplete, experienceComplete };

  return (
    <div className="w-full bg-white rounded-2xl md:border md:border-gray-200 md:p-8">
      <div className="mb-6">
        <div className="font-bold text-xl text-black">Profile Completion</div>
        <div className="text-gray-500 text-base mt-1">Complete your profile to get discovered</div>
      </div>
      {/* Progress Bar */}
      <div className="flex items-center mb-8">
        <div className="flex-1 mr-4">
          <div className="bg-white border border-gray-200 rounded-full w-full h-5 flex items-center">
            <div className="w-full h-full p-1">
              <div
                className="h-full bg-[#038B43] rounded-full transition-all duration-300"
                style={{ width: `${completionPercent}%` }}
              ></div>
            </div>
          </div>
        </div>
        <span className="text-gray-700 font-semibold text-base min-w-[40px]">{completionPercent}%</span>
      </div>
      {/* Checklist */}
      <div className="space-y-5">
        {checklist.map((item) => (
          <div
            key={item.key}
            className="flex flex-col items-start md:flex-row md:items-center md:justify-between p-6 bg-white border border-gray-200 rounded-xl shadow-sm gap-2"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                {item.icon}
              </span>
              <div>
                <div className="font-bold text-lg text-black">{item.label}</div>
                <div className="text-gray-500 text-base mt-1">{item.description}</div>
              </div>
            </div>
            {status[item.key as keyof typeof status] ? (
              <Check className="h-6 w-6 text-green-600" />
            ) : (
              <button
                className="text-black font-semibold text-base px-4 py-2 rounded hover:bg-gray-100 transition"
                onClick={() => navigate(item.route)}
              >
                Add
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileCompletionCardV2; 